/**
 * Interactive first-run tutorial (FTUE). A coach overlay that teaches the core swing by WATCHING the
 * real game state: it runs on top of a normal (range) session — the player uses the real controls —
 * and advances step-by-step as it sees them tee up, aim, lock power, and lock accuracy. Real-time
 * "TAP" prompts appear while the meter sweeps. Pure DOM/CSS; reads state + reports finish only (it
 * never touches the sim). main.ts hosts it (persists completion, fires analytics, runs the session).
 */
import type { GameState } from '../sim/types.ts';

export interface TutorialDeps {
  /** The tutorial ended: `completed` = reached the end, `false` = skipped. */
  onFinish(completed: boolean): void;
}

export interface Tutorial {
  /** Begin at the intro step (host has already started a range session). */
  start(): void;
  /** Advance from the latest state — call every frame. No-op while inactive. */
  update(view: GameState): void;
  active(): boolean;
}

type Step = 'intro' | 'selectClub' | 'aim' | 'power' | 'accuracy' | 'defense' | 'done';

const COPY: Record<Step, { title: string; text: string; button?: string }> = {
  intro: {
    title: 'Welcome to Fairway Clash',
    text: 'A real-time golf duel against an opponent — the lowest score when the clock runs out wins. Let’s learn the swing.',
    button: 'Let’s go ▶',
  },
  selectClub: { title: 'Step 1 · Tee up', text: 'Tap a club in the ATTACK column (left side) to step up to the tee.' },
  aim: { title: 'Step 2 · Aim', text: 'Drag from your golfer up toward the flag, then release to start your swing.' },
  power: { title: 'Step 3 · Power', text: 'The meter sweeps up — tap anywhere to lock your POWER.' },
  accuracy: { title: 'Step 4 · Accuracy', text: 'Now it sweeps back down — tap again to lock your ACCURACY.' },
  defense: {
    title: 'Step 5 · Defend',
    text: 'Attacking charged your defense! Tap a card in the DEFEND column (right side) to sabotage your rival’s green.',
  },
  done: {
    title: 'That’s the duel!',
    text: 'Aim, power, accuracy — and defense to disrupt your rival. You’ve got the basics. Now go win the match!',
    button: 'Play ▶',
  },
};

const STYLE_ID = 'fc-tut-style';
const CSS = `
.fc-tut{position:absolute;inset:0;z-index:45;pointer-events:none;display:none;font-family:var(--font-pixel,monospace);}
.fc-tut.is-on{display:block;}
.fc-tut-bar{position:absolute;bottom:calc(14px + env(safe-area-inset-bottom));left:50%;transform:translateX(-50%);
  width:min(360px,calc(100vw - 168px));background:rgba(8,16,22,.86);border:1px solid #2c4255;border-radius:12px;padding:11px 13px;
  color:#f4efe2;text-align:center;box-shadow:0 8px 24px rgba(0,0,0,.5);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);}
.fc-tut-title{font-size:11px;color:var(--gold,#f2c14e);margin-bottom:7px;letter-spacing:.02em;}
.fc-tut-text{font-size:10px;line-height:1.75;letter-spacing:.02em;}
.fc-tut-btns{margin-top:11px;}
.fc-tut-btn{pointer-events:auto;appearance:none;border:0;border-radius:999px;background:var(--gold,#f2c14e);color:#1a1205;
  font-family:inherit;font-size:10px;padding:9px 20px;cursor:pointer;}
.fc-tut-skip{pointer-events:auto;position:absolute;top:calc(14px + env(safe-area-inset-top));
  right:calc(12px + env(safe-area-inset-right));appearance:none;border:1px solid #2c4255;background:rgba(8,16,22,.6);
  color:#cfd9e2;border-radius:999px;font-family:inherit;font-size:9px;padding:6px 12px;cursor:pointer;}
.fc-tut-pulse{position:absolute;top:42%;left:50%;transform:translate(-50%,-50%);text-align:center;display:none;
  color:var(--gold,#f2c14e);text-shadow:0 2px 8px rgba(0,0,0,.7);animation:fc-tut-pulse .7s ease-in-out infinite;}
.fc-tut-pulse.is-on{display:block;}
.fc-tut-pulse b{display:block;font-size:30px;letter-spacing:.08em;}
.fc-tut-pulse span{display:block;font-size:11px;margin-top:5px;opacity:.92;}
@keyframes fc-tut-pulse{0%,100%{opacity:.8;transform:translate(-50%,-50%) scale(1);}50%{opacity:1;transform:translate(-50%,-50%) scale(1.12);}}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export function createTutorial(root: HTMLElement, deps: TutorialDeps): Tutorial {
  ensureStyle();

  const el = document.createElement('div');
  el.className = 'fc-tut';
  const bar = document.createElement('div');
  bar.className = 'fc-tut-bar';
  const title = document.createElement('div');
  title.className = 'fc-tut-title';
  const text = document.createElement('div');
  text.className = 'fc-tut-text';
  const btns = document.createElement('div');
  btns.className = 'fc-tut-btns';
  const actionBtn = document.createElement('button');
  actionBtn.type = 'button';
  actionBtn.className = 'fc-tut-btn';
  btns.appendChild(actionBtn);
  bar.append(title, text, btns);

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'fc-tut-skip';
  skip.textContent = 'Skip ✕';

  const pulse = document.createElement('div');
  pulse.className = 'fc-tut-pulse';
  const pulseBig = document.createElement('b');
  const pulseSub = document.createElement('span');
  pulse.append(pulseBig, pulseSub);

  el.append(bar, skip, pulse);
  root.appendChild(el);

  let isActive = false;
  let step: Step = 'intro';
  let shotsAtSwing = 0;
  let prevCharges = 0; // last frame's defense charges — a drop means a Defend card was played

  function render(): void {
    const c = COPY[step];
    title.textContent = c.title;
    text.textContent = c.text;
    if (c.button) {
      actionBtn.textContent = c.button;
      actionBtn.style.display = '';
    } else {
      actionBtn.style.display = 'none';
    }
  }

  function go(next: Step): void {
    step = next;
    render();
  }

  function finish(completed: boolean): void {
    if (!isActive) return;
    isActive = false;
    el.classList.remove('is-on');
    pulse.classList.remove('is-on');
    deps.onFinish(completed);
  }

  function setPulse(phase: GameState['swing']['phase']): void {
    if (phase === 'backswing') {
      pulseBig.textContent = 'TAP';
      pulseSub.textContent = '— set POWER —';
      pulse.classList.add('is-on');
    } else if (phase === 'impact') {
      pulseBig.textContent = 'TAP';
      pulseSub.textContent = '— set ACCURACY —';
      pulse.classList.add('is-on');
    } else {
      pulse.classList.remove('is-on');
    }
  }

  actionBtn.addEventListener('click', () => {
    if (step === 'intro') go('selectClub');
    else if (step === 'done') finish(true);
  });
  skip.addEventListener('click', () => finish(false));

  return {
    start(): void {
      isActive = true;
      step = 'intro';
      shotsAtSwing = 0;
      prevCharges = 0;
      render();
      pulse.classList.remove('is-on');
      el.classList.add('is-on');
    },
    update(view: GameState): void {
      if (!isActive) return;
      const phase = view.swing.phase;
      switch (step) {
        case 'selectClub':
          if (phase !== 'idle' && view.swing.clubIdx !== null) go('aim');
          break;
        case 'aim':
          if (phase === 'backswing') {
            shotsAtSwing = view.you.shots;
            go('power');
          }
          break;
        case 'power':
          if (phase === 'impact') go('accuracy');
          else if (phase === 'idle' || phase === 'aim') go('selectClub'); // swing abandoned
          break;
        case 'accuracy':
          if (view.you.shots > shotsAtSwing) go('defense'); // the shot fired → now teach defense
          else if (phase === 'backswing') go('power'); // they re-swung
          break;
        case 'defense':
          if (view.you.defenseCharges < prevCharges) go('done'); // a Defend card was played
          break;
        default:
          break;
      }
      // The TAP prompt follows the live meter phase, so practice swings keep getting cues.
      setPulse(phase);
      prevCharges = view.you.defenseCharges;
    },
    active(): boolean {
      return isActive;
    },
  };
}
